import React, { useState } from 'react';
import { defaultApiFetchErrorHandler } from '@scale/frontend-lib/api-fetch';
import useDebouncedValue from 'react-scale/hooks/useDebouncedValue';
import Autocomplete from '../../components/SimpleAutocomplete';
import HTTPError from '@scale/frontend-lib/httpError';
import { AutocompleteOption } from 'design-system/Autocomplete/Autocomplete';
import { useQuery } from 'react-query';
import { findOCPWorkflows } from 'react-scale/components/GrowthCenter/OnboardingControlPanel/api';

const queryKey = 'fetch-onboardingFlows'; // Arbitrary token to disambiguate lookups to useQuery.

async function fetchOnboardingFlows(text: string | undefined): Promise<AutocompleteOption[]> {
  try {
    const onboardingFlows = await findOCPWorkflows();

    // Filter the onboarding flows based on the search text
    const filteredOnboardingFlows = onboardingFlows.filter(({ name }: { name: string }) =>
      name.toLowerCase().includes(text?.toLowerCase() ?? ''),
    );

    return filteredOnboardingFlows.map(({ _id, name }: { _id: string; name: string }) => ({
      id: _id,
      title: name,
    })) as AutocompleteOption[];
  } catch (error) {
    defaultApiFetchErrorHandler(error as HTTPError, { handleHttp: true });
    return [];
  }
}

const OnboardingFlowAutocomplete: React.FC<{
  selectedFlow: { id: string; title: string };
  setSelectedFlow: (value: { id: string; title: string }) => void;
  disabled?: boolean;
}> = ({ selectedFlow, setSelectedFlow, disabled }) => {
  const [inputValue, setInputValue] = useState<string>('');
  const debouncedInputValue = useDebouncedValue(inputValue, 50);
  const queryResults = useQuery<AutocompleteOption[], Error>([queryKey, debouncedInputValue], () =>
    fetchOnboardingFlows(debouncedInputValue),
  );
  const options = queryResults.data ?? [];
  return (
    <>
      <Autocomplete
        className="w-full"
        options={options}
        value={{
          ...selectedFlow,
          title: selectedFlow.title.length
            ? selectedFlow.title
            : options.find(option => option.id === selectedFlow.id)?.title ?? '',
        }}
        setSelectedOption={setSelectedFlow}
        onInputChange={setInputValue}
        disabled={disabled}
        loading={queryResults.status === 'loading'}
        placeholder="Search Onboarding Flows by name..."
      />
    </>
  );
};

export default OnboardingFlowAutocomplete;
